import React, { useState, useEffect } from 'react';
import './styles/prisudregner.css';
import CTAKnap, { CTAIkoner } from './ctaknap';

const Prisudregner = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [needsGoogle, setNeedsGoogle] = useState('');
  const [selectedSEO, setSelectedSEO] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  
  // Kontaktformular data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    website: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const totalSteps = 5;

  // Base priser baseret på størrelse
  const basePrices = {
    'lille': 10000,
    'mellem': 20000,
    'stor': 50000
  };

  // Rabat priser (15% rabat på mellem størrelse)
  const discountPrices = {
    'lille': 10000,
    'mellem': 17000, // 20000 - 15% = 17000
    'stor': 50000
  };

  // Check om der er rabat kampagne aktiv
  const isDiscountActive = true; // Kan ændres til false for at slå rabat fra

  // SEO pakke priser
  const seoPrices = {
    'basic': 5000,
    'standard': 12000,
    'premium': 25000
  };

  // Service Schema for Google
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Professionelle Hjemmesider",
    "description": "Vi laver professionelle hjemmesider og SEO optimering for danske virksomheder",
    "provider": {
      "@type": "Organization",
      "name": "Parla Branding",
      "url": "https://www.parlabranding.dk"
    },
    "serviceType": "Web Development",
    "areaServed": {
      "@type": "Country",
      "name": "Danmark"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Hjemmeside Pakker",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Lille Hjemmeside",
            "description": "Op til 5 undersider"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "10000",
            "priceCurrency": "DKK"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Mellem Hjemmeside",
            "description": "Op til 15 undersider"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "20000",
            "priceCurrency": "DKK"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Stor Hjemmeside",
            "description": "+25 undersider"
          },
          "priceSpecification": {
            "@type": "PriceSpecification",
            "price": "50000",
            "priceCurrency": "DKK"
          }
        }
      ]
    }
  };

  // Beregn total pris løbende
  useEffect(() => {
    let price = 0;
    
    // Tilføj base pris hvis størrelse er valgt
    if (selectedSize) {
      const priceTable = isDiscountActive ? discountPrices : basePrices;
      price += priceTable[selectedSize];
    }
    
    // SEO er månedlig og vises separat
    
    setTotalPrice(price);
  }, [selectedSize, selectedSEO, needsGoogle]);

  // Nulstil SEO valg hvis brugeren ændrer Google valg til nej
  useEffect(() => {
    if (needsGoogle === 'nej') {
      setSelectedSEO('');
    }
  }, [needsGoogle]);

  const nextStep = () => {
    if (currentStep < totalSteps) {
      // Hvis man siger nej til Google på step 2, spring over SEO-pakker (step 3)
      if (currentStep === 2 && needsGoogle === 'nej') {
        setCurrentStep(4); // Spring til final step
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // Hvis man er på step 4 (final) og havde sagt nej til Google, gå tilbage til step 2
      if (currentStep === 4 && needsGoogle === 'nej') {
        setCurrentStep(2);
      } else {
        const newStep = currentStep - 1;
        setCurrentStep(newStep);
        
        // Nulstil alle valg hvis man går tilbage til step 1
        if (newStep === 1) {
          resetAllSelections();
        }
      }
    }
  };

  // Nulstil alle valg når brugeren går tilbage til step 1
  const resetAllSelections = () => {
    setSelectedSize('');
    setNeedsGoogle('');
    setSelectedSEO('');
  };

  // Håndter størrelse valg
  const handleSizeChange = (size) => {
    setSelectedSize(size);
    // Nulstil Google og SEO valg når størrelse ændres
    setNeedsGoogle('');
    setSelectedSEO('');
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedSize !== '';
      case 2: return needsGoogle !== '';
      case 3: return needsGoogle === 'ja' ? selectedSEO !== '' : true; // SEO pakke hvis ja, ellers altid true
      case 4: return true; // Final step
      default: return false;
    }
  };

  // Håndter formular input ændringer
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Håndter formular submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Forbered data til webhook
      const webhookData = {
        // Kontakt information
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        website: formData.website,
        message: formData.message,
        
        // Valgte pakker og priser
        selectedSize: selectedSize,
        sizeDescription: sizeOptions.find(s => s.id === selectedSize)?.desc,
        needsGoogle: needsGoogle,
        selectedSEO: selectedSEO,
        seoDescription: selectedSEO ? seoOptions.find(s => s.id === selectedSEO)?.desc : null,
        
        // Priser
        totalPrice: totalPrice,
        seoPrice: selectedSEO ? seoPrices[selectedSEO] : 0,
        
        // Metadata
        timestamp: new Date().toISOString(),
        source: 'prisudregner'
      };

      console.log('Sending data to webhook:', webhookData);

      // Send data til webhook
      const response = await fetch('https://n8n.srv886246.hstgr.cloud/webhook/d9b89c36-8071-44dc-b1e9-fca55d7773f1', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      console.log('Webhook response status:', response.status);
      console.log('Webhook response headers:', response.headers);

      // Tjek om response er OK eller acceptabel
      if (response.status >= 200 && response.status < 300) {
        console.log('Webhook succeeded with status:', response.status);
        setIsSubmitted(true);
      } else {
        // Prøv at læse error response
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.text();
          console.log('Error response:', errorData);
          errorMessage += ` - ${errorData}`;
        } catch (e) {
          console.log('Could not read error response');
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Du kan vælge at vise en fejlbesked til brugeren her
      alert('Der skete en fejl ved afsendelse. Prøv venligst igen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Valider om formular kan sendes
  const canSubmitForm = () => {
    return formData.name && formData.email && formData.phone;
  };

  const sizeOptions = [
    { 
      id: 'lille', 
      name: 'Lille', 
      desc: 'Op til 5 undersider',
      price: isDiscountActive ? discountPrices['lille'] : basePrices['lille'],
      originalPrice: basePrices['lille'],
      hasDiscount: false
    },
    { 
      id: 'mellem', 
      name: 'Mellem', 
      desc: 'Op til 15 undersider',
      price: isDiscountActive ? discountPrices['mellem'] : basePrices['mellem'],
      originalPrice: basePrices['mellem'],
      hasDiscount: isDiscountActive
    },
    { 
      id: 'stor', 
      name: 'Stor', 
      desc: '+25 undersider',
      price: isDiscountActive ? discountPrices['stor'] : basePrices['stor'],
      originalPrice: basePrices['stor'],
      hasDiscount: false
    }
  ];

  const googleOptions = [
    { 
      id: 'ja', 
      name: 'Ja, jeg vil findes på Google', 
      desc: 'SEO er vigtigt for mit firma'
    },
    { 
      id: 'nej', 
      name: 'Nej, det er ikke nødvendigt', 
      desc: 'Jeg får kunder på andre måder'
    }
  ];

  // Nulstil SEO valg når Google valg ændres
  const handleGoogleChange = (value) => {
    setNeedsGoogle(value);
    if (value === 'nej') {
      setSelectedSEO(''); // Nulstil SEO valg hvis brugeren siger nej til Google
    }
  };

  const seoOptions = [
    { 
      id: 'basic', 
      name: 'Basis SEO', 
      desc: 'Grundlæggende søgemaskineoptimering'
    },
    { 
      id: 'standard', 
      name: 'Standard SEO', 
      desc: 'Omfattende SEO strategi'
    },
    { 
      id: 'premium', 
      name: 'Premium SEO', 
      desc: 'Komplet SEO løsning'
    }
  ];

  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Vælg størrelse';
      case 2: return 'Har du brug for at blive fundet på Google?';
      case 3: return 'Vælg SEO pakke';
      case 4: return 'Dit tilbud';
      case 5: return 'Få præcist tilbud';
      default: return '';
    }
  };

  // Hjælpefunktion til at se om vi skal vise SEO-trinnet
  const shouldShowSEOStep = () => {
    return needsGoogle === 'ja';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="price-calculator__step-content">
            <div className="price-calculator__options-grid">
              {sizeOptions.map(size => (
                <div
                  key={size.id}
                  className={`price-calculator__option-card ${selectedSize === size.id ? 'price-calculator__option-card--selected' : ''} ${size.hasDiscount ? 'price-calculator__option-card--discount' : ''}`}
                  onClick={() => handleSizeChange(size.id)}
                >
                  <div className="price-calculator__option-content">
                    <span className="price-calculator__option-name">{size.name}</span>
                    <span className="price-calculator__option-desc">{size.desc}</span>
                  </div>
                  <div className="price-calculator__option-price">
                    {size.hasDiscount && (
                      <>
                        <span className="price-calculator__option-discount-badge">15% RABAT</span>
                        <span className="price-calculator__option-price-old">
                          {size.originalPrice.toLocaleString('da-DK')} kr.
                          <small className="price-calculator__tax-note">(ekskl. moms)</small>
                        </span>
                      </>
                    )}
                    <span className="price-calculator__option-price-current">
                      {size.price.toLocaleString('da-DK')} kr.
                      <small className="price-calculator__tax-note">(ekskl. moms)</small>
                    </span>
                  </div>
                  {selectedSize === size.id && (
                    <div className="price-calculator__option-check">
                      ✓
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="price-calculator__step-content">
            <div className="price-calculator__options-list">
              {googleOptions.map(option => (
                <div
                  key={option.id}
                  className={`price-calculator__option-item ${needsGoogle === option.id ? 'price-calculator__option-item--selected' : ''}`}
                  onClick={() => handleGoogleChange(option.id)}
                >
                  <div className="price-calculator__option-content">
                    <span className="price-calculator__option-name">{option.name}</span>
                    <span className="price-calculator__option-desc">{option.desc}</span>
                  </div>
                  <div className="price-calculator__option-check">
                    {needsGoogle === option.id && '✓'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 3:
        // Kun vis SEO-pakker hvis brugeren svarede ja til Google
        if (shouldShowSEOStep()) {
          return (
            <div className="price-calculator__step-content">
              <div className="price-calculator__seo-packages">
                {seoOptions.map(seo => (
                  <div
                    key={seo.id}
                    className={`price-calculator__seo-package ${selectedSEO === seo.id ? 'price-calculator__seo-package--selected' : ''}`}
                    onClick={() => setSelectedSEO(seo.id)}
                  >
                    <div className="price-calculator__seo-header">
                      <div className="price-calculator__seo-title">
                        <span className="price-calculator__seo-name">{seo.name}</span>
                        <span className="price-calculator__seo-price">
                          {seoPrices[seo.id].toLocaleString('da-DK')} kr. pr. måned
                          <small className="price-calculator__tax-note">(ekskl. moms)</small>
                        </span>
                      </div>
                      {selectedSEO === seo.id && (
                        <div className="price-calculator__seo-check">
                          ✓
                        </div>
                      )}
                    </div>
                    <p className="price-calculator__seo-desc">{seo.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        } else {
          // Hvis brugeren svarede nej til Google, spring over til final step
          return (
            <div className="price-calculator__step-content">
              <div className="price-calculator__skip-message">
                <p>Du springer SEO over og går direkte til dit tilbud.</p>
              </div>
            </div>
          );
        }
      
      case 4:
        return (
          <div className="price-calculator__step-content price-calculator__final-step">
            <div className="price-calculator__price-display">
              <div className="price-calculator__price-content">
                <span className="price-calculator__price-label">Hjemmeside (engangsbetaling)</span>
                <span className="price-calculator__price-value">
                  {totalPrice.toLocaleString('da-DK')} kr.
                </span>
                <span className="price-calculator__price-note">*Ekskl. moms</span>
              </div>
              {needsGoogle === 'ja' && selectedSEO && (
                <div className="price-calculator__seo-price-content">
                  <span className="price-calculator__price-label">SEO (månedlig betaling)</span>
                  <span className="price-calculator__price-value">
                    {seoPrices[selectedSEO].toLocaleString('da-DK')} kr.
                  </span>
                  <span className="price-calculator__price-note">*Pr. måned, ekskl. moms</span>
                </div>
              )}
            </div>
            
            <div className="price-calculator__selection-summary">
              <div className="price-calculator__summary-item">
                <span className="price-calculator__summary-label">Størrelse:</span>
                <span className="price-calculator__summary-value">{sizeOptions.find(s => s.id === selectedSize)?.name}</span>
              </div>
              {needsGoogle === 'ja' && selectedSEO && (
                <div className="price-calculator__summary-item">
                  <span className="price-calculator__summary-label">SEO:</span>
                  <span className="price-calculator__summary-value">{seoOptions.find(s => s.id === selectedSEO)?.name}</span>
                </div>
              )}
            </div>
          </div>
        );

      case 5:
        if (isSubmitted) {
          return (
            <div className="price-calculator__step-content price-calculator__success-step">
              <div className="price-calculator__success-message">
                <div className="price-calculator__success-icon">
                  ✓
                </div>
                <h5>Tak for din henvendelse!</h5>
                <p>Vi kontakter dig inden for 24 timer med dit præcise tilbud.</p>
                <div className="price-calculator__success-details">
                  <p><strong>Dit valg:</strong> {sizeOptions.find(s => s.id === selectedSize)?.name} hjemmeside</p>
                  {needsGoogle === 'ja' && selectedSEO && (
                    <p><strong>SEO:</strong> {seoOptions.find(s => s.id === selectedSEO)?.name}</p>
                  )}
                  <p><strong>Hjemmeside pris:</strong> {totalPrice.toLocaleString('da-DK')} kr. (ekskl. moms)</p>
                  {needsGoogle === 'ja' && selectedSEO && (
                    <p><strong>SEO pris:</strong> {seoPrices[selectedSEO].toLocaleString('da-DK')} kr. pr. måned (ekskl. moms)</p>
                  )}
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="price-calculator__step-content price-calculator__contact-step">
            <form className="price-calculator__contact-form" onSubmit={handleSubmit}>
              <div className="price-calculator__form-group">
                <label htmlFor="name">Navn *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Dit navn"
                />
              </div>

              <div className="price-calculator__form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="din@email.dk"
                />
              </div>

              <div className="price-calculator__form-group">
                <label htmlFor="phone">Telefon *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="12 34 56 78"
                />
              </div>

              <div className="price-calculator__form-group">
                <label htmlFor="company">Virksomhed</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  placeholder="Virksomhedens navn"
                />
              </div>
            </form>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="price-calculator">
      {/* Service Schema */}
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      
      <div className="price-calculator__header">
        <h3>Få dit tilbud</h3>
        <div className="price-calculator__progress-bar">
          <div className="price-calculator__progress-track">
            <div 
              className="price-calculator__progress-fill" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <span className="price-calculator__progress-text">
            Step {currentStep} af {totalSteps}
          </span>
        </div>
      </div>

      <div className="price-calculator__step-header">
        <h4 className="price-calculator__step-title">{getStepTitle()}</h4>
      </div>

      {renderStepContent()}

      {/* Live Price Display */}
      <div className={`price-calculator__live-price-display ${totalPrice > 0 ? 'price-calculator__live-price-display--visible' : 'price-calculator__live-price-display--hidden'}`}>
        <span className="price-calculator__live-price-label">Estimeret pris:</span>
        <span className="price-calculator__live-price-value">
          {totalPrice > 0 ? totalPrice.toLocaleString('da-DK') : '0'} kr.
          <small className="price-calculator__tax-note">(ekskl. moms)</small>
        </span>
      </div>

      {/* Navigation */}
      <div className="price-calculator__step-navigation">
        {currentStep > 1 && (
          <button 
            className="price-calculator__nav-btn price-calculator__nav-btn--back"
            onClick={prevStep}
          >
            <CTAIkoner.ArrowRight style={{ transform: 'rotate(180deg)' }} />
            Tilbage
          </button>
        )}
        
        {currentStep < totalSteps && (
          <button 
            className={`price-calculator__nav-btn price-calculator__nav-btn--next ${!canProceed() ? 'price-calculator__nav-btn--disabled' : ''}`}
            onClick={nextStep}
            disabled={!canProceed()}
          >
            Næste
            <CTAIkoner.ArrowRight />
          </button>
        )}

        {currentStep === 5 && !isSubmitted && (
          <button 
            type="submit"
            className={`price-calculator__nav-btn price-calculator__nav-btn--next ${!canSubmitForm() || isSubmitting ? 'price-calculator__nav-btn--disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!canSubmitForm() || isSubmitting}
          >
            {isSubmitting ? 'Sender...' : 'Send tilbud'}
            {!isSubmitting && <CTAIkoner.ArrowRight />}
          </button>
        )}

        {currentStep === 5 && isSubmitted && (
          <CTAKnap 
            variant="primary" 
            size="small"
            href="/cases"
            className="price-calculator__showcase-cta"
          >
            Se vores cases
          </CTAKnap>
        )}
      </div>
    </div>
  );
};

export default Prisudregner;
